<script setup lang="ts">
import { ref, watch } from 'vue';
import Modal from './Modal.vue';
import Button from '../forms/Button.vue';
import Field from '../forms/Field.vue';
import Input from '../forms/Input.vue';
import Select from '../forms/Select.vue';
import type { SelectOption } from '../forms/Select.vue';

// A support request FORM. Headless: it gathers input and emits a payload; the
// app submits it (today to whatever endpoint exists; tomorrow to the dedicated
// support service). No backend assumptions, no storage — the optional
// `reference` is passed in the moment and attached to the payload.
const props = withDefaults(
  defineProps<{
    open?: boolean;
    title?: string;
    categories?: SelectOption[];
    /** Optional single error/correlation ID — shown read-only, attached to payload. */
    reference?: string;
    categoryLabel?: string;
    subjectLabel?: string;
    subjectPlaceholder?: string;
    messageLabel?: string;
    messagePlaceholder?: string;
    referenceLabel?: string;
    submitLabel?: string;
    cancelLabel?: string;
    busy?: boolean;
    /** App-specific extra gate (e.g. a required consent checkbox) ANDed with the
     * built-in subject/message validation. */
    submitDisabled?: boolean;
    /**
     * The request landed (#425). The dialog stops being a form and becomes a
     * receipt: the fields go, and the footer offers one way onwards.
     *
     * It exists because the alternative the workspace shipped was worse — a
     * successful send navigated straight into the ticket that had just been
     * created, so the person was moved somewhere they did not ask to go, and the
     * only confirmation they got was the destination. An app that has nowhere to
     * send them simply leaves this false and closes with a toast, as before.
     */
    sent?: boolean;
    /** The receipt's sentence. Required in practice whenever `sent` is used. */
    sentMessage?: string;
    /** The single button on the receipt; emits `done`. */
    doneLabel?: string;
  }>(),
  { open: false, categories: () => [], busy: false, submitDisabled: false, sent: false },
);

const emit = defineEmits<{
  submit: [payload: { category?: string; subject: string; message: string; reference?: string }];
  cancel: [];
  /** The receipt's one button. The app decides where (if anywhere) that leads. */
  done: [];
}>();

const category = ref('');
const subject = ref('');
const message = ref('');

// Reset the form whenever the dialog (re)opens so a stale draft never leaks
// into the next request.
watch(
  () => props.open,
  (open) => {
    if (open) {
      category.value = '';
      subject.value = '';
      message.value = '';
    }
  },
);

// Dismissing the RECEIPT is not a cancellation — the request is already filed, so
// the app has to run the same close-and-move-on path its button runs.
function onDismiss() {
  if (props.sent) emit('done');
  else emit('cancel');
}

function onSubmit() {
  emit('submit', {
    category: props.categories.length ? category.value || undefined : undefined,
    subject: subject.value.trim(),
    message: message.value.trim(),
    reference: props.reference || undefined,
  });
}
</script>

<template>
  <Modal :open="open" :title="title" :dismissible="!busy" @close="onDismiss">
    <!-- The receipt (#425). Replaces the form rather than sitting under it: the
         request is gone, so the fields it was typed into are no longer something
         the person can act on. -->
    <p v-if="sent" class="lumi-support__sent" data-testid="support-sent">{{ sentMessage }}</p>

    <form v-else class="lumi-support" @submit.prevent="onSubmit">
      <p v-if="$slots.intro" class="lumi-support__intro"><slot name="intro" /></p>

      <Field v-if="categories.length" :label="categoryLabel">
        <Select v-model="category" :options="categories" :disabled="busy" />
      </Field>

      <Field :label="subjectLabel" required>
        <Input v-model="subject" :placeholder="subjectPlaceholder" :disabled="busy" />
      </Field>

      <Field :label="messageLabel" required>
        <textarea
          v-model="message"
          class="lumi-support__textarea"
          rows="5"
          :placeholder="messagePlaceholder"
          :disabled="busy"
        />
      </Field>

      <Field v-if="reference" :label="referenceLabel">
        <code class="lumi-support__ref">{{ reference }}</code>
      </Field>

      <!-- App-specific extra fields (e.g. a required consent acknowledgement). -->
      <slot name="extra" />
    </form>

    <template #footer>
      <Button v-if="sent" data-testid="support-sent-done" @click="emit('done')">
        {{ doneLabel }}
      </Button>
      <template v-else>
        <Button variant="secondary" :disabled="busy" @click="emit('cancel')">
          {{ cancelLabel }}
        </Button>
        <Button
          :loading="busy"
          :disabled="!subject.trim() || !message.trim() || submitDisabled"
          @click="onSubmit"
        >
          {{ submitLabel }}
        </Button>
      </template>
    </template>
  </Modal>
</template>

<style>
.lumi-support{display:flex;flex-direction:column;gap:var(--space-4);}
.lumi-support__sent{margin:0;font-size:var(--text-sm);color:var(--text-body);line-height:1.5;}
.lumi-support__intro{margin:0;font-size:var(--text-sm);color:var(--text-muted);line-height:1.5;}
.lumi-support__textarea{width:100%;font-family:var(--font-body);font-size:var(--text-sm);color:var(--text-body);background:var(--surface-card);border:1px solid var(--border-strong);border-radius:var(--radius-md);padding:8px 12px;resize:vertical;box-sizing:border-box;}
.lumi-support__textarea:focus{outline:none;border-color:var(--accent);}
.lumi-support__textarea:disabled{opacity:.5;cursor:not-allowed;}
.lumi-support__ref{display:inline-block;font-family:var(--font-mono);font-size:var(--text-xs);color:var(--text-muted);background:var(--surface-wash);border:1px solid var(--border-default);border-radius:var(--radius-sm);padding:4px 8px;}
</style>
